import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, EvidenceRow } from "../lib/api";
import { StatusPill } from "../components/StatusPill";

const LABEL_MAX = 120;

export function EvidenceDetail() {
  const nav = useNavigate();
  const { evidenceId } = useParams<{ evidenceId: string }>();
  const [row, setRow] = useState<EvidenceRow | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  // Label editing
  const [editingLabel, setEditingLabel] = useState(false);
  const [draftLabel, setDraftLabel] = useState("");
  const [savingLabel, setSavingLabel] = useState(false);
  const [labelError, setLabelError] = useState("");

  // Open/download
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState("");

  // Delete
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (!evidenceId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function tick() {
      try {
        const r = await api.get(evidenceId!);
        if (cancelled) return;
        setRow(r);
        const isTerminal = r.status === "finalised" && r.otsState === "anchored";
        if (!isTerminal && r.status !== "failed") {
          timer = setTimeout(tick, 4000);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    }
    void tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [evidenceId]);

  if (error) {
    return <div className="text-aigentico-red text-sm border border-aigentico-red bg-white px-4 py-3 rounded-md">{error}</div>;
  }

  if (!row) {
    return <div className="text-ink-faint text-sm uppercase tracking-widest py-24 text-center">Loading</div>;
  }

  const verifyUrl = `${window.location.origin}/verify/${row.evidenceId}`;
  const canOpen = row.status === "finalised" && !!row.immutableFileKey;

  function beginEditLabel() {
    setDraftLabel(row?.label ?? "");
    setLabelError("");
    setEditingLabel(true);
  }

  function cancelEditLabel() {
    setEditingLabel(false);
    setDraftLabel("");
    setLabelError("");
  }

  async function saveLabel() {
    if (!row) return;
    setSavingLabel(true);
    setLabelError("");
    try {
      const trimmed = draftLabel.trim();
      const nextLabel = trimmed === "" ? null : trimmed;
      const r = await api.putLabel(row.evidenceId, nextLabel);
      setRow(prev => prev ? { ...prev, label: r.label, updatedAt: r.updatedAt } : prev);
      setEditingLabel(false);
      setDraftLabel("");
    } catch (err) {
      setLabelError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingLabel(false);
    }
  }

  async function copyLink() {
    await navigator.clipboard.writeText(verifyUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function openOrDownload() {
    if (!row) return;
    setOpening(true);
    setOpenError("");
    try {
      const d = await api.getDownloadUrl(row.evidenceId);
      if (d.disposition === "inline") {
        // Route to browser/OS viewer.
        window.open(d.url, "_blank", "noopener");
      } else {
        // Force download via hidden anchor. The presigned URL already sets
        // Content-Disposition: attachment; filename=..., so the browser saves
        // with the original name.
        const a = document.createElement("a");
        a.href = d.url;
        a.rel = "noopener";
        // Some browsers require the download attribute to be set for a same-origin
        // consistent UX. It's a hint; the server-side Content-Disposition wins.
        a.download = d.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (err) {
      setOpenError(err instanceof Error ? err.message : String(err));
    } finally {
      setOpening(false);
    }
  }

  return (
    <div className="max-w-3xl">
      <Link to="/app" className="text-xs text-ink-faint uppercase tracking-widest hover:text-ink">← Back to evidence</Link>

      <div className="mt-6 flex items-start justify-between gap-6">
        <div className="min-w-0">
          <h2 className="mb-2 break-all">{row.filename || "(unnamed)"}</h2>
          <div className="text-sm text-ink-soft">
            {[
              row.mimeType,
              Number.isFinite(row.size) ? formatBytes(row.size as number) : null,
              row.createdAt ? `captured ${formatWhen(row.createdAt)}` : null,
            ].filter(Boolean).join(" · ")}
          </div>
        </div>
        <StatusPill row={row} />
      </div>

      <div className="mt-10 space-y-6">
        <Section title="Label">
          {editingLabel ? (
            <div>
              <input
                type="text"
                className="input"
                value={draftLabel}
                onChange={e => setDraftLabel(e.target.value.slice(0, LABEL_MAX))}
                maxLength={LABEL_MAX}
                autoFocus
                placeholder="e.g. Storm damage — kitchen roof"
                disabled={savingLabel}
              />
              <div className="flex items-center justify-between mt-2">
                <div className="text-xs text-ink-faint">
                  {draftLabel.length}/{LABEL_MAX} · empty removes the label
                </div>
                <div className="flex gap-2">
                  <button className="btn-secondary text-sm px-3 py-1" onClick={cancelEditLabel} disabled={savingLabel}>
                    Cancel
                  </button>
                  <button className="btn-primary text-sm px-3 py-1" onClick={saveLabel} disabled={savingLabel}>
                    {savingLabel ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
              {labelError && (
                <div className="text-aigentico-red text-xs mt-2">{labelError}</div>
              )}
            </div>
          ) : (
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                {row.label ? (
                  <div className="text-ink text-sm break-words">{row.label}</div>
                ) : (
                  <div className="text-ink-faint text-sm">No label yet.</div>
                )}
              </div>
              <button className="btn-secondary text-xs px-3 py-1 shrink-0" onClick={beginEditLabel}>
                {row.label ? "Edit" : "Add label"}
              </button>
            </div>
          )}
        </Section>

        <Section title="Original file">
          {canOpen ? (
            <div>
              <p className="text-sm text-ink-soft mb-3">
                The sealed copy lives in immutable storage. Opening fetches a short-lived
                link (15 min) directly from that copy.
              </p>
              <button className="btn-primary" onClick={openOrDownload} disabled={opening}>
                {opening ? "Preparing link…" : "Open / download"}
              </button>
              {openError && (
                <div className="text-aigentico-red text-xs mt-3">{openError}</div>
              )}
            </div>
          ) : (
            <p className="text-sm text-ink-faint">
              Available once finalisation completes.
            </p>
          )}
        </Section>

        <Section title="Evidence ID">
          <div className="hash">{row.evidenceId}</div>
        </Section>

        {row.sha256 && (
          <Section title="File hash (SHA-256)">
            <div className="hash">{row.sha256}</div>
            {row.declaredSha256 && row.declaredSha256 === row.sha256 && (
              <div className="text-xs text-verified mt-2 uppercase tracking-widest font-semibold">Matches client-declared hash</div>
            )}
          </Section>
        )}

        {row.manifestHash && (
          <Section title="Manifest hash">
            <div className="hash">{row.manifestHash}</div>
          </Section>
        )}

        {typeof row.ledgerSeqNo === "number" && (
          <Section title="Ledger">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-ink-faint mb-1">Sequence</div>
                <div className="mono">#{row.ledgerSeqNo}</div>
              </div>
              {row.ledgerRowHash && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-ink-faint mb-1">Row hash</div>
                  <div className="hash">{row.ledgerRowHash}</div>
                </div>
              )}
            </div>
          </Section>
        )}

        <Section title="Bitcoin anchor">
          {row.otsState === "anchored" ? (
            <div>
              <div className="text-verified text-sm uppercase tracking-widest mb-2 font-semibold">Anchored</div>
              {typeof row.otsBitcoinBlockHeight === "number" && (
                <div className="text-sm text-ink-soft">
                  Bitcoin block <span className="mono">#{row.otsBitcoinBlockHeight}</span>
                  {typeof row.otsBitcoinBlockTime === "number" && (
                    <>
                      , mined on{" "}
                      <span className="whitespace-nowrap">
                        {new Date(row.otsBitcoinBlockTime * 1000).toUTCString().replace(" GMT", " UTC")}
                      </span>
                    </>
                  )}
                </div>
              )}
              {row.otsBitcoinBlockHash && (
                <div className="text-xs text-ink-faint mt-2 break-all">
                  Block hash: <span className="mono">{row.otsBitcoinBlockHash}</span>
                </div>
              )}
              {typeof row.otsBitcoinBlockHeight === "number" && (
                <div className="mt-3">
                  <a
                    href={`https://mempool.space/block/${row.otsBitcoinBlockHash || row.otsBitcoinBlockHeight}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-verified text-sm font-semibold underline underline-offset-4 hover:no-underline"
                  >
                    View this block on the Bitcoin blockchain →
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="text-pending text-sm uppercase tracking-widest mb-2 font-semibold">Awaiting confirmation</div>
              <div className="text-sm text-ink-soft">
                Timestamp submitted to {row.otsCalendarsAccepted ?? 0} OpenTimestamps calendar{(row.otsCalendarsAccepted ?? 0) === 1 ? "" : "s"}.
                Bitcoin anchoring usually completes within 24 hours of upload. Your evidence is already sealed — the anchor adds independent Bitcoin verification.
              </div>
            </div>
          )}
        </Section>

        <Section title="Public verifier">
          <p className="text-sm text-ink-soft mb-3">
            Anyone with this link can verify the file, manifest, and Bitcoin timestamp.
            No account required.
          </p>
          <div className="flex gap-2">
            <input readOnly className="input font-mono text-xs" value={verifyUrl} />
            <button onClick={copyLink} className="btn-secondary shrink-0">
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </Section>

        <section className="panel border-aigentico-red">
          <div className="text-xs uppercase tracking-widest text-aigentico-red mb-3 font-semibold">Danger zone</div>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-ink-soft">
              <span className="font-semibold text-ink">Withdraw this evidence.</span> The row disappears from
              your list immediately, and the public verifier link stops resolving — anyone opening it will see
              “Evidence withdrawn” instead of the file details.
            </p>
            <p className="text-sm text-ink-soft">
              The cryptographic proof (SHA-256 fingerprint, ledger row, and Bitcoin anchor once confirmed)
              stays on the ledger and on the Bitcoin blockchain forever — those records are public and cannot
              be removed. The sealed file itself stays in immutable storage under Object Lock until its
              retention period expires, then a weekly cleanup job physically removes it.
            </p>
            <p className="text-sm text-ink-soft">
              This does not remove copies of the file that already exist outside StampmatS — including any
              downloads you or people you shared with have already taken.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => { setDeleteError(""); setDeleteConfirm(""); setDeleteOpen(true); }}
                className="btn-danger text-sm px-4 py-2 shrink-0"
                disabled={deleting}
              >
                Withdraw evidence
              </button>
            </div>
          </div>
        </section>
      </div>

      {deleteOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-6 z-20" onClick={() => !deleting && setDeleteOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="text-lg font-semibold text-ink">Withdraw this evidence?</div>
            <p className="text-sm text-ink-soft">
              The verifier link will stop resolving. The ledger and blockchain proof stay intact but cannot
              be brought back into your list from the app. Type
              <span className="font-mono text-aigentico-red mx-1">DELETE</span>
              to confirm.
            </p>
            <input
              autoFocus
              className="input"
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              disabled={deleting}
            />
            {deleteError && (
              <div className="text-aigentico-red text-xs">{deleteError}</div>
            )}
            <div className="flex gap-2 justify-end">
              <button className="btn-secondary text-sm px-4 py-2" onClick={() => setDeleteOpen(false)} disabled={deleting}>
                Cancel
              </button>
              <button
                className="btn-danger text-sm px-4 py-2"
                disabled={deleting || deleteConfirm !== "DELETE"}
                onClick={async () => {
                  if (!row) return;
                  setDeleting(true);
                  setDeleteError("");
                  try {
                    await api.deleteMine(row.evidenceId);
                    nav("/app?deleted=1", { replace: true });
                  } catch (err) {
                    setDeleteError(err instanceof Error ? err.message : String(err));
                    setDeleting(false);
                  }
                }}
              >
                {deleting ? "Withdrawing…" : "Withdraw"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="panel">
      <div className="text-xs uppercase tracking-widest text-aigentico-navy mb-3 font-semibold">{title}</div>
      {children}
    </section>
  );
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "size unavailable";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
